import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import {
  ChartBarIcon, UsersIcon, FolderIcon, CurrencyDollarIcon,
  ClockIcon, ArrowDownTrayIcon,
  ArrowTrendingUpIcon, ArrowTrendingDownIcon, CheckCircleIcon, XMarkIcon
} from '@heroicons/react/24/outline';
import {
  Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale,
  LinearScale, BarElement, LineElement, PointElement, Title, Filler
} from 'chart.js';
import { Doughnut, Bar, Line } from 'react-chartjs-2';
import dayjs from 'dayjs';
import { PageHeader } from '../../components/layout';
import {
  Card, CardBody, CardHeader, CardTitle, Button, Select, PageLoader,
  Tabs, TabList, Tab, TabPanels, TabPanel, Input, Badge, ProgressBar
} from '../../components/ui';
import { reportService } from '../../services';
import toast from 'react-hot-toast';

ChartJS.register(
  ArcElement, Tooltip, Legend, CategoryScale,
  LinearScale, BarElement, LineElement, PointElement, Title, Filler
);

export default function Reports() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('projects');
  const [loading, setLoading] = useState(true);
  const [projectReport, setProjectReport] = useState(null);
  const [clientReport, setClientReport] = useState(null);
  const [financialReport, setFinancialReport] = useState(null);
  const [timeReport, setTimeReport] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    startDate: dayjs().subtract(30, 'days').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
    status: '',
    client: '',
    groupBy: 'month'
  });

  useEffect(() => {
    fetchReports();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, activeTab]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        ...(filters.status && { status: filters.status }),
        ...(filters.client && { client: filters.client }),
        ...(filters.groupBy && { groupBy: filters.groupBy })
      };

      const promises = [
        reportService.getProjectReport(params),
        reportService.getClientReport(params),
        reportService.getFinancialReport(params)
      ];

      if (activeTab === 'time') {
        promises.push(reportService.getTimeReport(params));
      }

      const [projects, clients, financial, time] = await Promise.all(promises);
      setProjectReport(projects);
      setClientReport(clients);
      setFinancialReport(financial);
      if (time) setTimeReport(time);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      toast.error(t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({
      startDate: dayjs().subtract(30, 'days').format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
      status: '',
      client: '',
      groupBy: 'month'
    });
  };

  const chartColors = {
    primary: '#1D4ED8',
    secondary: '#14B8A6',
    accent: '#FBBF24',
    success: '#10B981',
    danger: '#EF4444',
    warning: '#F59E0B',
    neutral: '#6B7280',
    purple: '#8B5CF6',
    pink: '#EC4899'
  };

  const getProjectStatusData = () => {
    if (!projectReport?.summary?.byStatus) return null;
    const labels = Object.keys(projectReport.summary.byStatus).map(key =>
      t(`projects.statuses.${key}`) || key
    );
    const data = Object.values(projectReport.summary.byStatus);

    return {
      labels,
      datasets: [{
        data,
        backgroundColor: [
          chartColors.neutral,
          chartColors.success,
          chartColors.accent,
          chartColors.primary,
          chartColors.danger
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    };
  };

  const getProjectProgressData = () => {
    if (!projectReport?.projects) return null;

    const progressRanges = {
      '0-25': 0,
      '26-50': 0,
      '51-75': 0,
      '76-100': 0
    };

    projectReport.projects.forEach(project => {
      const progress = project.progress || 0;
      if (progress <= 25) progressRanges['0-25']++;
      else if (progress <= 50) progressRanges['26-50']++;
      else if (progress <= 75) progressRanges['51-75']++;
      else progressRanges['76-100']++;
    });

    return {
      labels: ['0-25%', '26-50%', '51-75%', '76-100%'],
      datasets: [{
        label: t('common.progress') || 'Progress',
        data: Object.values(progressRanges),
        backgroundColor: [
          chartColors.danger,
          chartColors.warning,
          chartColors.accent,
          chartColors.success
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    };
  };

  const getClientStatusData = () => {
    if (!clientReport?.summary?.byStatus) return null;
    const labels = Object.keys(clientReport.summary.byStatus).map(key =>
      t(`clients.statuses.${key}`) || key
    );
    const data = Object.values(clientReport.summary.byStatus);

    return {
      labels,
      datasets: [{
        data,
        backgroundColor: [
          chartColors.success,
          chartColors.accent,
          chartColors.primary,
          chartColors.danger
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    };
  };

  const getClientIndustryData = () => {
    if (!clientReport?.summary?.byIndustry) return null;
    const labels = Object.keys(clientReport.summary.byIndustry);
    const data = Object.values(clientReport.summary.byIndustry);

    return {
      labels,
      datasets: [{
        label: t('clients.clients') || 'Clients',
        data,
        backgroundColor: chartColors.primary,
        borderColor: chartColors.primary,
        borderWidth: 2
      }]
    };
  };

  const getFinancialTimelineData = () => {
    if (!financialReport?.timeline) return null;

    return {
      labels: financialReport.timeline.map(t => t.period),
      datasets: [
        {
          label: t('reports.revenue') || 'Revenue',
          data: financialReport.timeline.map(t => t.revenue),
          backgroundColor: chartColors.success,
          borderColor: chartColors.success,
          borderWidth: 2,
          fill: false
        },
        {
          label: t('reports.expenses') || 'Expenses',
          data: financialReport.timeline.map(t => t.expenses),
          backgroundColor: chartColors.danger,
          borderColor: chartColors.danger,
          borderWidth: 2,
          fill: false
        },
        {
          label: t('reports.netIncome') || 'Net Income',
          data: financialReport.timeline.map(t => t.revenue - t.expenses),
          backgroundColor: chartColors.primary,
          borderColor: chartColors.primary,
          borderWidth: 2,
          fill: false,
          borderDash: [5, 5]
        }
      ]
    };
  };

  const getExpensesByCategoryData = () => {
    if (!financialReport?.expensesByCategory) return null;
    const labels = Object.keys(financialReport.expensesByCategory);
    const data = Object.values(financialReport.expensesByCategory);

    return {
      labels,
      datasets: [{
        data,
        backgroundColor: [
          chartColors.primary,
          chartColors.secondary,
          chartColors.accent,
          chartColors.success,
          chartColors.danger,
          chartColors.warning,
          chartColors.purple,
          chartColors.pink
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    };
  };

  const getTimeByUserData = () => {
    if (!timeReport?.byUser) return null;

    return {
      labels: timeReport.byUser.map(u => `${u.user?.firstName} ${u.user?.lastName}`),
      datasets: [{
        label: t('reports.hours') || 'Hours',
        data: timeReport.byUser.map(u => (u.total / 60).toFixed(1)),
        backgroundColor: chartColors.primary,
        borderColor: chartColors.primary,
        borderWidth: 2
      }]
    };
  };

  const getTimeByProjectData = () => {
    if (!timeReport?.byProject) return null;

    return {
      labels: timeReport.byProject.map(p => p.project?.name),
      datasets: [{
        label: t('reports.hours') || 'Hours',
        data: timeReport.byProject.map(p => (p.total / 60).toFixed(1)),
        backgroundColor: chartColors.secondary,
        borderColor: chartColors.secondary,
        borderWidth: 2
      }]
    };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatHours = (minutes) => {
    return (minutes / 60).toFixed(1);
  };

  const statusOptions = [
    { value: '', label: t('common.all') },
    { value: 'draft', label: t('projects.statuses.draft') },
    { value: 'active', label: t('projects.statuses.active') },
    { value: 'on_hold', label: t('projects.statuses.onHold') },
    { value: 'completed', label: t('projects.statuses.completed') },
    { value: 'cancelled', label: t('projects.statuses.cancelled') }
  ];

  const groupByOptions = [
    { value: 'week', label: t('reports.weekly') || 'Weekly' },
    { value: 'month', label: t('reports.monthly') || 'Monthly' },
    { value: 'year', label: t('reports.yearly') || 'Yearly' }
  ];

  if (loading) {
    return <PageLoader />;
  }

  return (
    <>
      <Helmet>
        <title>{t('reports.title')} - ProCTM</title>
      </Helmet>

      <PageHeader
        title={t('reports.title')}
        actions={
          <Button variant="outline" icon={ArrowDownTrayIcon}>
            {t('reports.export') || 'Export Report'}
          </Button>
        }
      />

      {/* Filters */}
      <Card className="mb-6">
        <CardBody>
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 flex-1">
              <Input
                label={t('reports.startDate') || 'Start Date'}
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
              <Input
                label={t('reports.endDate') || 'End Date'}
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
              {activeTab === 'projects' && (
                <Select
                  label={t('common.status')}
                  options={statusOptions}
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                />
              )}
              {activeTab === 'financial' && (
                <Select
                  label={t('reports.groupBy') || 'Group By'}
                  options={groupByOptions}
                  value={filters.groupBy}
                  onChange={(e) => handleFilterChange('groupBy', e.target.value)}
                />
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={resetFilters} icon={XMarkIcon}>
                {t('common.clearFilters') || 'Clear'}
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      <Tabs>
        <TabList className="mb-6">
          <Tab active={activeTab === 'projects'} onClick={() => setActiveTab('projects')}>
            <FolderIcon className="w-4 h-4 mr-2" />
            {t('reports.projectReport')}
          </Tab>
          <Tab active={activeTab === 'clients'} onClick={() => setActiveTab('clients')}>
            <UsersIcon className="w-4 h-4 mr-2" />
            {t('reports.clientReport')}
          </Tab>
          <Tab active={activeTab === 'financial'} onClick={() => setActiveTab('financial')}>
            <CurrencyDollarIcon className="w-4 h-4 mr-2" />
            {t('reports.financialReport')}
          </Tab>
          <Tab active={activeTab === 'time'} onClick={() => setActiveTab('time')}>
            <ClockIcon className="w-4 h-4 mr-2" />
            {t('reports.timeReport') || 'Time Tracking'}
          </Tab>
        </TabList>

        <TabPanels>
          {/* Projects Tab */}
          <TabPanel active={activeTab === 'projects'}>
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardBody>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">{t('reports.totalProjects') || 'Total Projects'}</p>
                      <p className="text-3xl font-bold text-primary">{projectReport?.summary?.total || 0}</p>
                    </div>
                    <FolderIcon className="w-10 h-10 text-primary/20" />
                  </div>
                </CardBody>
              </Card>
              <Card>
                <CardBody>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">{t('reports.avgProgress') || 'Avg Progress'}</p>
                      <p className="text-3xl font-bold text-secondary">{projectReport?.summary?.avgProgress || 0}%</p>
                    </div>
                    <ArrowTrendingUpIcon className="w-10 h-10 text-secondary/20" />
                  </div>
                </CardBody>
              </Card>
              <Card>
                <CardBody>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">{t('reports.totalBudget') || 'Total Budget'}</p>
                      <p className="text-3xl font-bold text-accent">{formatCurrency(projectReport?.summary?.totalBudget || 0)}</p>
                    </div>
                    <CurrencyDollarIcon className="w-10 h-10 text-accent/20" />
                  </div>
                </CardBody>
              </Card>
              <Card>
                <CardBody>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">{t('reports.completedOnTime') || 'On Time'}</p>
                      <p className="text-3xl font-bold text-success">
                        {projectReport?.summary?.completedOnTime || 0}
                        <span className="text-sm text-gray-500">/{projectReport?.summary?.completedOnTime + projectReport?.summary?.completedLate || 0}</span>
                      </p>
                    </div>
                    <CheckCircleIcon className="w-10 h-10 text-success/20" />
                  </div>
                </CardBody>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t('reports.projectsByStatus') || 'Projects by Status'}</CardTitle>
                </CardHeader>
                <CardBody>
                  {getProjectStatusData() ? (
                    <div className="h-80">
                      <Doughnut
                        data={getProjectStatusData()}
                        options={{
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { position: 'bottom' },
                            tooltip: {
                              callbacks: {
                                label: (context) => {
                                  const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                  const percentage = ((context.parsed / total) * 100).toFixed(1);
                                  return `${context.label}: ${context.parsed} (${percentage}%)`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">{t('reports.noData') || 'No data available'}</p>
                  )}
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{t('reports.projectsByProgress') || 'Projects by Progress'}</CardTitle>
                </CardHeader>
                <CardBody>
                  {getProjectProgressData() ? (
                    <div className="h-80">
                      <Bar
                        data={getProjectProgressData()}
                        options={{
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { display: false },
                            tooltip: {
                              callbacks: {
                                label: (context) => `${context.parsed.y} ${t('reports.projects') || 'projects'}`
                              }
                            }
                          },
                          scales: {
                            y: { beginAtZero: true, ticks: { stepSize: 1 } }
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">{t('reports.noData') || 'No data available'}</p>
                  )}
                </CardBody>
              </Card>
            </div>

            {/* Project List */}
            {projectReport?.projects && projectReport.projects.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('reports.projectDetails') || 'Project Details'}</CardTitle>
                </CardHeader>
                <CardBody>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 font-semibold text-sm">{t('common.name')}</th>
                          <th className="text-left py-3 px-4 font-semibold text-sm">{t('common.status')}</th>
                          <th className="text-left py-3 px-4 font-semibold text-sm">{t('common.progress')}</th>
                          <th className="text-left py-3 px-4 font-semibold text-sm">{t('projects.budget')}</th>
                          <th className="text-left py-3 px-4 font-semibold text-sm">{t('projects.client')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {projectReport.projects.slice(0, 10).map((project) => (
                          <tr key={project._id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4 font-medium">{project.name}</td>
                            <td className="py-3 px-4">
                              <Badge variant={project.status === 'completed' ? 'success' : 'neutral'}>
                                {t(`projects.statuses.${project.status}`) || project.status}
                              </Badge>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <ProgressBar value={project.progress || 0} size="sm" className="flex-1" />
                                <span className="text-sm font-medium w-12 text-right">{project.progress || 0}%</span>
                              </div>
                            </td>
                            <td className="py-3 px-4">{formatCurrency(project.budget || 0)}</td>
                            <td className="py-3 px-4">{project.client?.name || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardBody>
              </Card>
            )}
          </TabPanel>

          {/* Clients Tab */}
          <TabPanel active={activeTab === 'clients'}>
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardBody>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">{t('reports.totalClients') || 'Total Clients'}</p>
                      <p className="text-3xl font-bold text-primary">{clientReport?.summary?.total || 0}</p>
                    </div>
                    <UsersIcon className="w-10 h-10 text-primary/20" />
                  </div>
                </CardBody>
              </Card>
              <Card>
                <CardBody>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">{t('reports.avgScore') || 'Avg Score'}</p>
                      <p className="text-3xl font-bold text-secondary">{clientReport?.summary?.avgScore || 0}</p>
                    </div>
                    <ArrowTrendingUpIcon className="w-10 h-10 text-secondary/20" />
                  </div>
                </CardBody>
              </Card>
              <Card>
                <CardBody>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">{t('reports.totalRevenue') || 'Total Revenue'}</p>
                      <p className="text-3xl font-bold text-success">{formatCurrency(clientReport?.summary?.totalRevenue || 0)}</p>
                    </div>
                    <CurrencyDollarIcon className="w-10 h-10 text-success/20" />
                  </div>
                </CardBody>
              </Card>
              <Card>
                <CardBody>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">{t('reports.topClients') || 'Top Clients'}</p>
                      <p className="text-3xl font-bold text-accent">{clientReport?.summary?.topClients?.length || 0}</p>
                    </div>
                    <ChartBarIcon className="w-10 h-10 text-accent/20" />
                  </div>
                </CardBody>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t('reports.clientsByStatus') || 'Clients by Status'}</CardTitle>
                </CardHeader>
                <CardBody>
                  {getClientStatusData() ? (
                    <div className="h-80">
                      <Doughnut
                        data={getClientStatusData()}
                        options={{
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { position: 'bottom' },
                            tooltip: {
                              callbacks: {
                                label: (context) => {
                                  const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                  const percentage = ((context.parsed / total) * 100).toFixed(1);
                                  return `${context.label}: ${context.parsed} (${percentage}%)`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">{t('reports.noData') || 'No data available'}</p>
                  )}
                </CardBody>
              </Card>

              {getClientIndustryData() && Object.keys(clientReport?.summary?.byIndustry || {}).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>{t('reports.clientsByIndustry') || 'Clients by Industry'}</CardTitle>
                  </CardHeader>
                  <CardBody>
                    <div className="h-80">
                      <Bar
                        data={getClientIndustryData()}
                        options={{
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { display: false },
                            tooltip: {
                              callbacks: {
                                label: (context) => `${context.parsed.y} ${t('clients.clients') || 'clients'}`
                              }
                            }
                          },
                          scales: {
                            y: { beginAtZero: true, ticks: { stepSize: 1 } }
                          }
                        }}
                      />
                    </div>
                  </CardBody>
                </Card>
              )}
            </div>

            {/* Top Clients */}
            {clientReport?.summary?.topClients && clientReport.summary.topClients.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('reports.topClients') || 'Top Clients by Revenue'}</CardTitle>
                </CardHeader>
                <CardBody>
                  <div className="space-y-4">
                    {clientReport.summary.topClients.map((client, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-semibold">{client.name}</p>
                            <p className="text-sm text-gray-500">
                              {client.projectCount} {t('projects.title') || 'projects'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-lg text-success">{formatCurrency(client.revenue || 0)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            )}
          </TabPanel>

          {/* Financial Tab */}
          <TabPanel active={activeTab === 'financial'}>
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardBody>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">{t('reports.revenue') || 'Revenue'}</p>
                      <p className="text-3xl font-bold text-success">{formatCurrency(financialReport?.summary?.revenue || 0)}</p>
                    </div>
                    <ArrowTrendingUpIcon className="w-10 h-10 text-success/20" />
                  </div>
                </CardBody>
              </Card>
              <Card>
                <CardBody>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">{t('reports.expenses') || 'Expenses'}</p>
                      <p className="text-3xl font-bold text-danger">{formatCurrency(financialReport?.summary?.totalExpenses || 0)}</p>
                    </div>
                    <ArrowTrendingDownIcon className="w-10 h-10 text-danger/20" />
                  </div>
                </CardBody>
              </Card>
              <Card>
                <CardBody>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">{t('reports.netIncome') || 'Net Income'}</p>
                      <p className={`text-3xl font-bold ${(financialReport?.summary?.netIncome || 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                        {formatCurrency(financialReport?.summary?.netIncome || 0)}
                      </p>
                    </div>
                    {(financialReport?.summary?.netIncome || 0) >= 0 ? (
                      <ArrowTrendingUpIcon className="w-10 h-10 text-success/20" />
                    ) : (
                      <ArrowTrendingDownIcon className="w-10 h-10 text-danger/20" />
                    )}
                  </div>
                </CardBody>
              </Card>
              <Card>
                <CardBody>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">{t('reports.outstanding') || 'Outstanding'}</p>
                      <p className="text-3xl font-bold text-warning">{formatCurrency(financialReport?.summary?.outstanding || 0)}</p>
                    </div>
                    <CurrencyDollarIcon className="w-10 h-10 text-warning/20" />
                  </div>
                </CardBody>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>{t('reports.revenueVsExpenses') || 'Revenue vs Expenses Timeline'}</CardTitle>
                </CardHeader>
                <CardBody>
                  {getFinancialTimelineData() ? (
                    <div className="h-96">
                      <Line
                        data={getFinancialTimelineData()}
                        options={{
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { position: 'top' },
                            tooltip: {
                              callbacks: {
                                label: (context) => `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`
                              }
                            }
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: {
                                callback: (value) => formatCurrency(value)
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">{t('reports.noData') || 'No data available'}</p>
                  )}
                </CardBody>
              </Card>

              {getExpensesByCategoryData() && Object.keys(financialReport?.expensesByCategory || {}).length > 0 && (
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle>{t('reports.expensesByCategory') || 'Expenses by Category'}</CardTitle>
                  </CardHeader>
                  <CardBody>
                    <div className="h-80">
                      <Doughnut
                        data={getExpensesByCategoryData()}
                        options={{
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { position: 'bottom' },
                            tooltip: {
                              callbacks: {
                                label: (context) => `${context.label}: ${formatCurrency(context.parsed)}`
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </CardBody>
                </Card>
              )}
            </div>
          </TabPanel>

          {/* Time Tracking Tab */}
          <TabPanel active={activeTab === 'time'}>
            {timeReport ? (
              <>
                {/* Statistics Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <Card>
                    <CardBody>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500 mb-1">{t('reports.totalHours') || 'Total Hours'}</p>
                          <p className="text-3xl font-bold text-primary">{formatHours(timeReport.summary.totalTime || 0)}h</p>
                        </div>
                        <ClockIcon className="w-10 h-10 text-primary/20" />
                      </div>
                    </CardBody>
                  </Card>
                  <Card>
                    <CardBody>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500 mb-1">{t('reports.billableHours') || 'Billable Hours'}</p>
                          <p className="text-3xl font-bold text-success">{formatHours(timeReport.summary.billableTime || 0)}h</p>
                        </div>
                        <CheckCircleIcon className="w-10 h-10 text-success/20" />
                      </div>
                    </CardBody>
                  </Card>
                  <Card>
                    <CardBody>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500 mb-1">{t('reports.nonBillableHours') || 'Non-Billable'}</p>
                          <p className="text-3xl font-bold text-neutral">{formatHours(timeReport.summary.nonBillableTime || 0)}h</p>
                        </div>
                        <ClockIcon className="w-10 h-10 text-neutral/20" />
                      </div>
                    </CardBody>
                  </Card>
                  <Card>
                    <CardBody>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500 mb-1">{t('reports.billableRate') || 'Billable Rate'}</p>
                          <p className="text-3xl font-bold text-accent">
                            {timeReport.summary.totalTime > 0
                              ? ((timeReport.summary.billableTime / timeReport.summary.totalTime) * 100).toFixed(1)
                              : 0}%
                          </p>
                        </div>
                        <ChartBarIcon className="w-10 h-10 text-accent/20" />
                      </div>
                    </CardBody>
                  </Card>
                </div>

                {/* Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {getTimeByUserData() && timeReport.byUser.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle>{t('reports.timeByUser') || 'Time by User'}</CardTitle>
                      </CardHeader>
                      <CardBody>
                        <div className="h-80">
                          <Bar
                            data={getTimeByUserData()}
                            options={{
                              maintainAspectRatio: false,
                              plugins: {
                                legend: { display: false },
                                tooltip: {
                                  callbacks: {
                                    label: (context) => `${context.parsed.y}h`
                                  }
                                }
                              },
                              scales: {
                                y: { beginAtZero: true }
                              }
                            }}
                          />
                        </div>
                      </CardBody>
                    </Card>
                  )}

                  {getTimeByProjectData() && timeReport.byProject.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle>{t('reports.timeByProject') || 'Time by Project'}</CardTitle>
                      </CardHeader>
                      <CardBody>
                        <div className="h-80">
                          <Bar
                            data={getTimeByProjectData()}
                            options={{
                              maintainAspectRatio: false,
                              plugins: {
                                legend: { display: false },
                                tooltip: {
                                  callbacks: {
                                    label: (context) => `${context.parsed.y}h`
                                  }
                                }
                              },
                              scales: {
                                y: { beginAtZero: true }
                              }
                            }}
                          />
                        </div>
                      </CardBody>
                    </Card>
                  )}
                </div>
              </>
            ) : (
              <Card>
                <CardBody>
                  <p className="text-gray-500 text-center py-8">{t('reports.noData') || 'No data available'}</p>
                </CardBody>
              </Card>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </>
  );
}